import { ReactElement, useEffect, useState } from "react";
import { Checkbox, Radio, Select } from "antd";
import type { SelectProps } from "antd";

import "./kyc-details.scss";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import TabsComponent from "../../components/tabs-component/tabs-component";
import AccountDetails from "./components/account-details/account-details";
import ProfessionalInformation from "./components/professional-information/professional-information";
import CommunicationAddress from "./components/communication-address/communication-address";
import Preferences from "./components/preferences/preferences";
import FinancialInformation from "./components/financial-information/financial-information";
import FatcaAndCrs from "./components/fatca-crs/fatca-crs";
import Documents from "./components/documents/documents";
// import KeyImage from "../../assets/key.svg";
import ShieldImage from "../../assets/shield-security.svg";
import EditImage from "../../assets/edit-option.svg";
import DocumentImage from "../../assets/document-cloud.svg";
import UploadImage from "../../assets/document-upload.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import instance from "../../store/axios";
import { AxiosResponse } from "axios";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Dashboard from "./components/dashboard/dashboard";
import WaitingApprovalService from "../waiting-approvals/waiting-approval.service";
import LoadingComponent from "../../components/loading/loading.component";
import { ADD_TO_BREADCRUMB, LOADING } from "../../store/actions";
import { saveAs } from "file-saver";
import DashboardProvider from "src/context/dashboard-provider";
import CustomModal from "src/components/custom-modal/custom-modal";
import RequestsTransactions from "./components/requests-transactions/requests-transactions";

interface AccountDetailsProps {
  [key: string]: any;
}

const KycDetails: React.FC = (): ReactElement => {
  const { accountId, reviewId, activeKyc } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const queryParams = new URLSearchParams(location.search);
  const witingApprovalService = new WaitingApprovalService();
  const isApproveCase = queryParams.get("approve");
  const [activeTab, setActiveTab] = useState<string>(
    queryParams.get("tab") || "0"
  );
  const [accountKycDetails, setAccountKycDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [openGenerateKycModal, setOpenGenerateKycModal] =
    useState<boolean>(false);
  const [allowSendEmail, setAllowSendEmail] = useState<boolean>(false);
  const [allowSync, setAllowSync] = useState<boolean>(false);
  const [openOptionalDepositModal, setOpenOptionalDepositModal] =
    useState<boolean>(false);
  const [selectedOptionalDeposit, setSelectedOptionalDeposit] = useState<
    string[]
  >([]);

  const OPTIONAL_DEPOSIT_PORTFOLIOS: { value: string; label: string }[] = [
    { value: "ND", label: "Managed by Client" },
    { value: "CT", label: "Custody Trustee" },
    { value: "DD", label: "Managed by Company" },
    { value: "CU", label: "Custody" },
  ];

  useEffect(() => {
    getKycApi();
  }, []);

  let items: any = [
    {
      key: "0",
      label: `Dashboard`,
      children: <Dashboard />,
    },
    {
      key: "1",
      label: `Transactions`,
      children: <RequestsTransactions accountKycDetails={accountKycDetails} />,
    },
    {
      key: "2",
      label: `Personal details`,
      children: <AccountDetails accountKycDetails={accountKycDetails} />,
    },
    {
      key: "3",
      label: `Documents`,
      children: <Documents accountKycDetails={accountKycDetails} />,
    },
    {
      key: "4",
      label: `Professional details`,
      children: (
        <ProfessionalInformation accountKycDetails={accountKycDetails} />
      ),
    },
    {
      key: "5",
      label: `Communication address`,
      children: <CommunicationAddress accountKycDetails={accountKycDetails} />,
    },
    {
      key: "6",
      label: `Preferences`,
      children: <Preferences accountKycDetails={accountKycDetails} />,
    },
    {
      key: "7",
      label: `Financial details`,
      children: <FinancialInformation accountKycDetails={accountKycDetails} />,
    },
    {
      key: "8",
      label: `FATCA & CRS`,
      children: <FatcaAndCrs accountKycDetails={accountKycDetails} />,
    },
  ];

  const navigateToEditPages = (url: any = null): void => {
    if (url) {
      navigate(url);
      return;
    }
    switch (activeTab) {
      case "2":
        navigate(
          `/account-details/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "3":
        localStorage.removeItem("docType");
        navigate(
          `/document/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "4":
        navigate(
          `/professional-details/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "5":
        navigate(
          `/communication-address/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "6":
        navigate(
          `/preference/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "7":
        navigate(
          `/financial/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      case "8":
        navigate(
          `/crs/${accountKycDetails.id}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
      default:
        navigate(
          `/account-details/${accountKycDetails.id}/edit${isApproveCase ? "?approve=1" : ""
          }`
        );
        break;
    }
  };

  const generateKycPdf = async (): Promise<void> => {
    const response: any = await witingApprovalService.generateKycPdf(
      +accountId,
      allowSendEmail,
      allowSync || allowSendEmail
    );

    if (response) {
      downloadPdf(response?.data?.data, "kyc.pdf");
      setOpenGenerateKycModal(false);
    }
  };

  const downloadPdf = (base64String, fileName) => {
    // Convert Base64 to Blob
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    // Create a link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "document.pdf"; // Default file name

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  const actions: SelectProps["options"] = [
    {
      value: "KYC versions",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() => navigate(`/kyc-versions/${accountId}`)}
        >
          <img src={DocumentImage} /> <span>KYC versions</span>
        </div>
      ),
    },
    {
      value: "Document versions",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() => navigate(`/document-versions/${accountId}`)}
        >
          <img src={DocumentImage} /> <span>Document versions</span>
        </div>
      ),
    },
    {
      value: "Generate KYC PDF",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() => setOpenGenerateKycModal(true)}
        >
          <img src={DocumentImage} /> <span>Generate KYC PDF</span>
        </div>
      ),
    },
    // {
    //   value: "Send KYC by email",
    //   label: (
    //     <div
    //       className="select-options flex align-center"
    //       onClick={sendKycEmail}
    //     >
    //       <img src={DocumentImage} /> <span>Send KYC by email</span>
    //     </div>
    //   ),
    // },
  ];

  if (user?.permissions?.includes("kyc_tools")) {
    actions.unshift({
      value: "Edit",
      label:
        activeTab !== "3" ? (
          <div
            className="select-options flex align-center"
            onClick={() => navigateToEditPages()}
          >
            <img src={EditImage} /> <span>Edit</span>
          </div>
        ) : (
          <div
            className="select-options flex align-center"
            onClick={() => navigateToEditPages()}
          >
            <img src={UploadImage} alt="" />
            <span>Upload new document</span>
          </div>
        ),
    });

    if (user?.permissions?.includes("port_creation_skip_deposit")) {
      actions.push({
        value: "Make Deposit Optional",
        label: (
          <div
            className="select-options flex align-center"
            onClick={() => {
              setSelectedOptionalDeposit(accountKycDetails?.optionalDeposit || []);
              setOpenOptionalDepositModal(true);
            }}
          >
            <img src={EditImage} /> <span>Make portfolio deposit optional</span>
          </div>
        ),
      });
    }

  }

  const downloadDocument = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/generate-pdf`
      );

      const base64String = response.data?.data;

      // Convert the base64 string to a Blob
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Save the Blob as a PDF file
      saveAs(blob, `${accountKycDetails?.accountInfo[0]?.nameEn}.pdf`);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      if (isApproveCase) {
        localStorage.setItem(
          "star_prev_url",
          `/home/business-account/${accountId}/${activeKyc}/${reviewId}/details?tab=${activeTab || "0"
          }&approve=1`
        );
      } else {
        localStorage.setItem(
          "star_prev_url",
          `/home/business-account/${accountId}/${activeKyc}/details`
        );
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const reviewOptions: SelectProps["options"] = [
    {
      value: "Lock record",
      label: (
        <div className="select-options flex align-center">
          <img src={ShieldImage} /> <span>Lock record</span>
        </div>
      ),
    },
    {
      value: "Release record",
      label: (
        <div className="select-options flex align-center">
          <img src={ShieldImage} /> <span>Release record</span>
        </div>
      ),
    },
    {
      value: "take action",
      label: (
        <div
          className="select-options flex align-center"
          onClick={() =>
            navigate(
              `/customers/review-form/${accountId}/${accountKycDetails?.activeKycId ||
              accountKycDetails?.accountInfo[0]?.id
              }/${reviewId}`
            )
          }
        >
          <img src={UploadImage} alt="" />
          <span>Take action</span>
        </div>
      ),
    },
  ];

  if (user?.permissions?.includes("kyc_tools")) {
    reviewOptions.unshift({
      value: "Edit",
      label:
        activeTab !== "3" ? (
          <div
            className="select-options flex align-center"
            onClick={() => navigateToEditPages()}
          >
            <img src={EditImage} /> <span>Edit</span>
          </div>
        ) : (
          <div
            className="select-options flex align-center"
            onClick={() => navigateToEditPages()}
          >
            <img src={UploadImage} alt="" />
            <span>Upload new document</span>
          </div>
        ),
    });
    if (activeTab === "2") {
      reviewOptions.push({
        value: "Edit contact persons details",
        label: (
          <div
            className="select-options flex align-center"
            onClick={() =>
              navigateToEditPages(
                `/account-details/${accountKycDetails?.id}/${activeKyc}/edit-contact-persons`
              )
            }
          >
            <img src={EditImage} /> <span>Edit contact persons details</span>
          </div>
        ),
      });
    }
    if (activeTab === "7") {
      reviewOptions.push({
        value: "Edit bank accounts details",
        label: (
          <div
            className="select-options flex align-center"
            onClick={() =>
              navigateToEditPages(
                `/financial/${accountKycDetails?.id}/${activeKyc}/edit-banks`
              )
            }
          >
            <img src={EditImage} /> <span>Edit bank accounts details</span>
          </div>
        ),
      });
    }

    if (accountKycDetails?.accountCode) {
      reviewOptions.push({
        value: "View KYC Document",
        label: (
          <div
            className="select-options flex align-center"
            onClick={downloadDocument}
          >
            <img src={ShieldImage} /> <span>View KYC Document</span>
          </div>
        ),
      });
    }
  }

  const onTabChange = (activeKey: string): void => {
    setActiveTab(activeKey);
  };

  const getKycApi = async (): Promise<void> => {
    setLoading(true);

    try {
      const response: AxiosResponse<AccountDetailsProps> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      dispatch({
        type: ADD_TO_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `${response?.data?.data?.accountInfo[0]?.nameEn ||
                response?.data?.data?.bankAccounts[0]?.beneficiaryName
                } `,
              type: "link",
              link: `/home/business-account/${response?.data?.data?.accountInfo[0]?.accountId}/${response?.data?.data?.activeKycId}/details`,
            },
          ],
        },
      });
      setAccountKycDetails(response?.data?.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      if (isApproveCase) {
        localStorage.setItem(
          "star_prev_url",
          `/home/business-account/${accountId}/${activeKyc}/${reviewId}/details?tab=${activeTab || "0"
          }&approve=1`
        );
      } else {
        localStorage.setItem(
          "star_prev_url",
          `/home/business-account/${accountId}/${activeKyc}/details`
        );
      }
    }

    setLoading(false);
  };

  const handleActionChange = (value: string, kycReviewId: number): void => {
    if (value === "Lock record") {
      lockKycReview(kycReviewId);
    } else if (value === "Release record") {
      releaseKycReview(kycReviewId);
    }
  };

  const toggleOptionalDeposit = (code: string): void => {
    setSelectedOptionalDeposit((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const saveOptionalDeposit = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });
    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/update-capabilities`,
        { optionalDeposit: selectedOptionalDeposit }
      );
      toast.success("Account updated successfully");
      setOpenOptionalDepositModal(false);
      getKycApi();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
    dispatch({ type: LOADING, payload: false });
  };

  const lockKycReview = async (kycReviewId: number): Promise<void> => {
    const response = await witingApprovalService.lockKycReviewRecord(
      kycReviewId
    );
    if (response) {
      getKycApi();
    }
  };

  const releaseKycReview = async (kycReviewId: number): Promise<void> => {
    const response = await witingApprovalService.releaseKycReviewRecord(
      kycReviewId
    );
    if (response) {
      navigate(`/customers/waiting-approvals`);
    }
  };

  return loading ? (
    <LoadingComponent />
  ) : (
    <DashboardProvider>
      <div className="kyc-details-container flex column">
        <div className="flex align-center justify-space-between wrap">
          <BreadcrumbComponent />
          <Select
            allowClear
            showSearch
            options={
              isApproveCase
                ? reviewOptions.filter((option) =>
                  accountKycDetails?.accountInfo[0]?.locked
                    ? option.value !== "Lock record"
                    : option.value !== "Release record"
                )
                : activeTab === "2"
                  ? [
                    ...actions,
                    {
                      value: "Edit contact persons details",
                      label: (
                        <div
                          className="select-options flex align-center"
                          onClick={() =>
                            navigateToEditPages(
                              `/account-details/${accountKycDetails?.id}/${activeKyc}/edit-contact-persons`
                            )
                          }
                        >
                          <img src={EditImage} />{" "}
                          <span>Edit contact persons details</span>
                        </div>
                      ),
                    },
                  ]
                  : activeTab === "7"
                    ? [
                      ...actions,
                      {
                        value: "Edit bank accounts details",
                        label: (
                          <div
                            className="select-options flex align-center"
                            onClick={() =>
                              navigateToEditPages(
                                `/financial/${accountKycDetails?.id}/${activeKyc}/edit-banks`
                              )
                            }
                          >
                            <img src={EditImage} />{" "}
                            <span>Edit bank accounts details</span>
                          </div>
                        ),
                      },
                    ]
                    : activeTab === "0" || activeTab === "1"
                      ? actions.filter((action) => action.value !== "Edit")
                      : actions
            }
            value={null}
            onChange={(value: string) =>
              handleActionChange(value, parseInt(reviewId as string))
            }
            placeholder={<div className="flex">Select an action</div>}
          />
        </div>

        <TabsComponent
          items={items}
          activeTab={activeTab}
          onChange={onTabChange}
        />
      </div>
      <CustomModal
        isVisible={openGenerateKycModal}
        centered
        showClose={false}
        onClose={() => {
          setOpenGenerateKycModal(false);
        }}
        className="generate-modal"
      >
        <div className="w-full h-full py-5 bg-white rounded-xl shadow-[0px_0px_4px_0px_rgba(0,0,0,0.16)] flex-col justify-center items-end gap-5 inline-flex">
          <div className="flex-col justify-start items-end gap-10 flex">
            <div className="flex-col justify-start items-start flex">
              <div className="self-stretch h-7 flex-col justify-start items-start gap-5 flex">
                <div className="self-stretch h-7 px-5 flex-col justify-start items-start gap-4 flex">
                  <div className="self-stretch justify-start items-start gap-4 inline-flex">
                    <div className="grow shrink basis-0 text-black text-2xl font-bold font-['Montserrat'] tracking-tight">
                      Generate KYC PDF
                    </div>
                    <div className="w-6 h-6 justify-center items-center flex">
                      <div className="w-6 h-6 relative"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch flex-col justify-start items-start flex">
                <div className="px-5 justify-start items-start gap-5 inline-flex">
                  <div className="self-stretch py-5 flex-col justify-start items-start gap-5 inline-flex">
                    <div className="self-stretch text-[#535353] text-sm font-medium font-['Montserrat'] leading-7">
                      You're trying to generate KYC PDF, please select on of the
                      below options
                    </div>
                    <Radio.Group
                      defaultValue={0}
                      className="flex flex-col gap-3"
                      onChange={(e) => {
                        if (e.target.value === 2) {
                          setAllowSendEmail(true);
                          setAllowSync(true);
                        } else {
                          setAllowSendEmail(false);
                          setAllowSync(false);
                        }
                        if (e.target.value === 1) {
                          setAllowSync(true);
                        } else {
                          setAllowSync(false);
                        }
                        if (e.target.value === 0) {
                          setAllowSendEmail(false);
                          setAllowSync(false);
                        }
                      }}
                    >
                      <Radio value={0}>View only</Radio>
                      <Radio value={1}>Upload to PROSPERO only</Radio>
                      <Radio value={2}>
                        Upload to PROSPERO and send a copy to the client by
                        email
                      </Radio>
                    </Radio.Group>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch px-5 justify-end items-start gap-5 inline-flex">
            <div
              onClick={() => setOpenGenerateKycModal(false)}
              className="cursor-pointer grow shrink basis-0 h-12 p-4 bg-white rounded-lg border border-[#002f6c] justify-center items-center gap-2.5 flex"
            >
              <div className="text-center text-[#002f6c] text-sm font-bold font-['Montserrat'] leading-none">
                Cancel
              </div>
            </div>
            <div
              onClick={generateKycPdf}
              className="cursor-pointer grow shrink basis-0 h-12 p-3 bg-[#002f6c] rounded-lg justify-center items-center gap-2 flex"
            >
              <div className="text-center text-white text-sm font-bold font-['Montserrat'] leading-none">
                Submit
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isVisible={openOptionalDepositModal}
        centered
        width={520}
        showClose={true}
        onClose={() => setOpenOptionalDepositModal(false)}
        className="optional-deposit-modal"
      >
        <div className="flex flex-col px-6">
          <div className="flex flex-col items-center text-center mb-5">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <img src="/images/deposit.svg" alt="deposit" className="w-9 h-9" />
            </div>
            <h3 className="text-lg font-bold text-black mb-1">
              Make Deposit Optional
            </h3>
            <p className="text-gray-500 text-sm max-w-md">
              Select the portfolios for which deposit should be optional.
            </p>
          </div>

          <div className="flex flex-col gap-2 mb-2">
            {OPTIONAL_DEPOSIT_PORTFOLIOS.map((portfolio) => {
              const isChecked = selectedOptionalDeposit.includes(
                portfolio.value
              );
              return (
                <label
                  key={portfolio.value}
                  className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-all ${isChecked
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                    }`}
                >
                  <span className="text-sm font-medium text-black">
                    {portfolio.label}
                  </span>
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleOptionalDeposit(portfolio.value)}
                  />
                </label>
              );
            })}
          </div>

          <div className="flex justify-center gap-3 mt-5">
            <button
              type="button"
              onClick={() => setOpenOptionalDepositModal(false)}
              className="rounded-lg bg-white min-w-[160px] px-10 py-2.5 text-blue-500 text-sm font-semibold border-[1px] border-blue-500 hover:bg-blue-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveOptionalDeposit}
              className="rounded-lg bg-blue-500 min-w-[160px] px-10 py-2.5 text-white text-sm font-semibold hover:bg-blue-600 transition-colors shadow-sm"
            >
              Save
            </button>
          </div>
        </div>
      </CustomModal>
    </DashboardProvider>
  );
};

export default KycDetails;
